import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GuardiaScheda from "@/components/GuardiaScheda";
import Tesserino from "@/components/Tesserino";
import DocumentiPersonali from "@/components/DocumentiPersonali";
import { GuardiaFormDialog } from "@/components/GuardiaFormDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function GuardiaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { data: g, isLoading } = useQuery({
    queryKey: ["guardia", id],
    queryFn: async () => (await api.get(`/guardie/${id}`)).data,
  });

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/guardie/${id}`);
      toast.success("Guardia eliminata");
      navigate("/anagrafica");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading || !g) return <p className="text-sm text-slate-400">Caricamento…</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" className="-ml-2 text-slate-600" onClick={() => navigate("/anagrafica")} data-testid="back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Torna all'anagrafica
        </Button>
        {isManager && (
          <Dialog open={delOpen} onOpenChange={setDelOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-guardia-button">
                <Trash2 className="mr-2 h-4 w-4" /> Elimina
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Elimina guardia</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">Confermi l'eliminazione di <strong>{g.cognome} {g.nome}</strong>? L'operazione è irreversibile.</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDelOpen(false)}>Annulla</Button>
                <Button onClick={remove} disabled={deleting} data-testid="confirm-delete-guardia" className="bg-red-600 hover:bg-red-700">
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Elimina definitivamente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mb-4">
        <Badge
          variant="outline"
          data-testid="quota-status-badge"
          className={g.quota_pagata ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"}
        >
          Quota associativa {g.quota_anno}: {g.quota_pagata ? "Pagata" : "Non pagata"}
        </Badge>
      </div>

      <GuardiaScheda
        g={g}
        actions={
          isManager && (
            <GuardiaFormDialog
              initial={g}
              trigger={
                <Button size="sm" data-testid="edit-guardia-button" className="bg-[#14382C] hover:bg-[#0E281F]">
                  <Pencil className="mr-2 h-4 w-4" /> Modifica dati
                </Button>
              }
            />
          )
        }
      />
      <div className="mt-6"><Tesserino g={g} canEdit={isManager} /></div>
      <DocumentiPersonali guardiaId={g.id} />
    </div>
  );
}
